import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, CircularProgress, debounce } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import * as yup from 'yup';
import { useFormik } from 'formik';
import axios from 'axios';
const DialogEditCliente = ({ onClose, isFatura, lotes, initial, ...props }) => {
    const [loading, setLoading] = useState(false);
    const getInfoCodigoPostal = async (cp4, cp3) => {
        return new Promise((resolve, reject) => {
            if (cp4.length === 4) {
                if (cp3.length === 3) {
                    axios
                        .post('/api/public/getLocalidade', {
                        cp4: cp4,
                        cp3: cp3
                    })
                        .then(r => {
                        resolve(r.data);
                    })
                        .catch(e => {
                        reject(e);
                    });
                }
                else {
                    axios
                        .post('/api/public/getLocalidade', {
                        cp4: cp4,
                        cp3: null
                    })
                        .then(r => {
                        resolve(r.data);
                    })
                        .catch(e => {
                        reject(e);
                    });
                }
            }
        });
    };
    const formik = useFormik({
        initialValues: {},
        validationSchema: yup.object({
            nome: yup.string().required('Nome é obrigatório'),
            nif: yup.string().notRequired(),
            email: yup.string().email('Email inválido').notRequired(),
            morada: yup.string().required('Morada é obrigatória'),
            contacto: yup.string().nullable(),
            cep: yup
                .string()
                .matches(/^\d{4}-\d{3}$/, 'Código Postal deve estar no formato XXXX-YYY')
                .notRequired(),
            localidade: yup.string().required('O campo é obrigatório').max(255, 'Limite de caracteres ultrapassado'),
            concelho: yup.string().required('Concelho é obrigatório'),
            distrito: yup
                .string()
                .required('O campo é obrigatório')
                .test('is-valid-distrito', 'Distrito sem lote associado', value => {
                if (isFatura)
                    return true;
                return lotes.some(l => l.distrito.toUpperCase() == value.toUpperCase());
            })
        }),
        validateOnMount: false,
        onSubmit: async (values) => {
            onClose(values, values.loteId);
        }
    });
    const handleCEPChange = useCallback(debounce(async (cepValue) => {
        const regex = /^\d{4}-\d{3}$/;
        if (!regex.test(cepValue))
            return;
        setLoading(true); // start loading
        try {
            const [cp4, cp3] = cepValue.split('-');
            const cep = await getInfoCodigoPostal(cp4, cp3);
            if (cep) {
                formik.setFieldValue('localidade', cep.result);
                formik.setFieldValue('concelho', cep.concelho);
                formik.setFieldValue('distrito', cep.distrito);
            }
            if (!isFatura) {
                const lote = lotes.find(l => l.distrito.toUpperCase() === cep.distrito.toUpperCase());
                // Update hidden loteId
                formik.setFieldValue('loteId', lote?.id);
                // Update distrito error
                if (!lote) {
                    formik.setFieldError('distrito', 'Distrito sem lote associado');
                }
                else {
                    formik.setFieldError('distrito', undefined);
                }
            }
            else {
                formik.setFieldError('distrito', undefined);
                formik.setFieldValue('loteId', undefined);
            }
        }
        catch (err) {
            formik.setFieldError('cep', 'Código Postal inválido');
            formik.setFieldValue('localidade', '');
            formik.setFieldValue('concelho', '');
            formik.setFieldValue('distrito', '');
        }
        finally {
            setLoading(false); // stop loading
        }
    }, 500), []);
    // CEP input change
    const handleCEPInputChange = (value) => {
        formik.setFieldValue('cep', value);
        handleCEPChange(value);
    };
    const validateCEP = async (initial) => {
        if (initial && initial.cep) {
            try {
                let cps = initial.cep.split('-');
                let cep = await getInfoCodigoPostal(cps[0], cps[1]);
                if (cep) {
                    formik.setFieldValue('localidade', cep.result, true);
                    formik.setFieldValue('concelho', cep.concelho, true);
                    formik.setFieldValue('distrito', cep.distrito, true);
                }
                if (!isFatura) {
                    const lote = lotes.find(r => r.distrito.toUpperCase() == cep.distrito.toUpperCase());
                    if (lote) {
                        formik.setFieldValue('loteId', lote.id, true);
                    }
                    else {
                        formik.setFieldValue('loteId', undefined, true);
                    }
                }
            }
            catch (error) {
                console.log(error);
            }
        }
    };
    const normalize = (src) => ({
        nome: src?.nome ?? '',
        nif: src?.nif ?? '',
        contacto: src?.contacto ?? '',
        email: src?.email ?? '',
        morada: src?.morada ?? '',
        cep: src?.cep ?? '',
        localidade: src?.localidade ?? '',
        concelho: src?.concelho ?? '',
        distrito: src?.distrito ?? '',
        loteId: src?.loteId
    });
    useEffect(() => {
        const v = normalize(initial);
        formik.resetForm({ values: v });
        if (v.cep)
            void validateCEP(v);
    }, [initial]);
    return (<Dialog {...props}>
      <form>
        <DialogTitle>Dados de {isFatura ? 'faturação' : 'cliente'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid lg={12} md={12} xs={12}>
              <TextField label='Nome' name='nome' required value={formik.values?.nome ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.nome && Boolean(formik.errors.nome)} helperText={formik.touched.nome && formik.errors.nome}/>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid lg={3} md={3} xs={12}>
              <TextField label='NIF' name='nif' value={formik.values?.nif ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.nif && Boolean(formik.errors.nif)} helperText={formik.touched.nif && formik.errors.nif}/>
            </Grid>
            <Grid lg={3} md={3} xs={12}>
              <TextField label='Contacto' name='contacto' value={formik.values?.contacto ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.contacto && Boolean(formik.errors.contacto)} helperText={formik.touched.contacto && formik.errors.contacto}/>
            </Grid>
            <Grid lg={6} md={6} xs={12}>
              <TextField label='Email' name='email' value={formik.values?.email ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.email && Boolean(formik.errors.email)} helperText={formik.touched.email && formik.errors.email}/>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid lg={12} md={12} xs={12}>
              <TextField label='Morada' name='morada' value={formik.values?.morada ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.morada && Boolean(formik.errors.morada)} helperText={formik.touched.morada && formik.errors.morada}/>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid lg={3} md={2} xs={12}>
              <TextField label='Código Postal' name='cep' value={formik.values?.cep ?? ''} onChange={e => handleCEPInputChange(e.target.value)} onBlur={formik.handleBlur} fullWidth required error={formik.touched.cep && Boolean(formik.errors.cep)} helperText={formik.touched.cep && formik.errors.cep} InputProps={{
            endAdornment: <React.Fragment>{loading ? <CircularProgress color='inherit' size={20}/> : null}</React.Fragment>
        }}/>
            </Grid>
            <Grid lg={3} md={4} xs={12}>
              <TextField label='Localidade' name='localidade' value={formik.values?.localidade ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required disabled={loading} error={formik.touched.localidade && Boolean(formik.errors.localidade)} helperText={formik.touched.localidade && formik.errors.localidade} InputProps={{
            endAdornment: <React.Fragment>{loading ? <CircularProgress color='inherit' size={20}/> : null}</React.Fragment>
        }}/>
            </Grid>
            <Grid lg={3} md={3} xs={12}>
              <TextField label='Concelho' name='concelho' value={formik.values?.concelho ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required disabled={loading} error={formik.touched.concelho && Boolean(formik.errors.concelho)} helperText={formik.touched.concelho && formik.errors.concelho} InputProps={{
            endAdornment: <React.Fragment>{loading ? <CircularProgress color='inherit' size={20}/> : null}</React.Fragment>
        }}/>
            </Grid>
            <Grid lg={3} md={3} xs={12}>
              <TextField label='Distrito' name='distrito' value={formik.values?.distrito ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required disabled={loading} error={formik.touched.distrito && Boolean(formik.errors.distrito)} helperText={formik.touched.distrito && formik.errors.distrito} InputProps={{
            endAdornment: <React.Fragment>{loading ? <CircularProgress color='inherit' size={20}/> : null}</React.Fragment>
        }}/>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button type='button' onClick={() => onClose(undefined)}>
            Cancelar
          </Button>
          <Button onClick={() => {
            formik.handleSubmit();
        }} disabled={!formik.isValid || !formik.dirty || formik.isSubmitting} variant='contained'>
            OK
          </Button>
        </DialogActions>
      </form>
    </Dialog>);
};
export default DialogEditCliente;
